import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CONFIG } from "../../constants";

import logoutIcon from "url:shared-assets/icons/logout.svg";
import accountIcon from "url:shared-assets/icons/account.svg";
import supportIcon from "url:shared-assets/icons/support.svg";
import aiIcon from "url:shared-assets/icons/ai.svg";
import swedishFlag from "url:shared-assets/icons/swedish.svg";
import englishFlag from "url:shared-assets/icons/english.svg";
import { useAtom } from "jotai";
import { languageAtom } from "../../jotai_state";

interface MenuItem {
  id: number;
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
  divider?: boolean;
  badge?: boolean;
}

interface Props {
  changeLanguage: () => void;
  debug: boolean;
}

const NavMini = (props: Props) => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t } = translation;
  const [lang] = useAtom(languageAtom);
  const { logout } = useAuth0();

  const calculateMenuItems = () => {
    let items: Array<MenuItem> = [];

    items.push({
      id: 6,
      title: t("my-account"),
      onClick: () => navigate("/account"),
      icon: <img src={accountIcon} alt="account" />,
    });

    items.push({
      id: 7,
      title: t("menu-support"),
      onClick: () => navigate("/support"),
      icon: <img src={supportIcon} alt="support" />,
      divider: true,
    });
    if (props.debug && CONFIG.SYSTEM !== "production" && CONFIG.USER_TYPE === "client") {
      items.push({
        id: 8,
        title: "AI system (debug)",
        onClick: () => navigate("/ai_system"),
        icon: <img src={aiIcon} alt="AI" />,
        divider: true,
      });
    }
    items.push({
      id: 9,
      title: lang === "en" ? "SWE" : "ENG",
      onClick: props.changeLanguage,
      icon: (
        <img src={lang === "en" ? swedishFlag : englishFlag} alt="language" />
      ),
    });

    return items;
  };

  const combinedMenuItems = calculateMenuItems();

  const clientLogout = () => {
    logout({
      logoutParams: {
        clientId: CONFIG.AUTH0_CLIENT_ID,
        returnTo: CONFIG.APP_HOST,
      },
    }).then();
  };

  return (
    <div className="bg-indigo-800 p-2 w-68 rounded-md">
      <div className="grid gap-y-2 w-full">
        {combinedMenuItems.map((item) => (
          <div key={item.id}>
            <div
              className="flex items-center gap-x-4 p-2 z-title2 hover:bg-indigo-700 text-white cursor-pointer rounded-md"
              onClick={() => {
                item.onClick();
              }}
            >
              {item.icon}
              <span>{item.title}</span>
              {item.badge && (
                <div className="rounded-full bg-cherry-500 h-2 w-2" />
              )}
            </div>
            {item.divider && <hr className="border-indigo-700 mt-2" />}
          </div>
        ))}
      </div>

      {/* Logout button as a footer inside the navbar */}
      <div className="w-full">
        <hr className="border-indigo-700 w-full my-2" />
        <div
          className="flex items-center gap-x-4 p-2 z-title2 hover:bg-indigo-700 text-white hover:text-white cursor-pointer w-full rounded-md"
          onClick={() => clientLogout()}
        >
          <img src={logoutIcon} alt="logout" />
          <span>{t("logout")}</span>
        </div>
      </div>
    </div>
  );
};

export default NavMini;
